import { useTranslation } from '@/lib/i18n';
import type { FundingDashboard, Money } from '@/lib/api-admin/types';

const formatMoney = (money: Money | undefined | null): string => {
  if (!money) return '—';
  return `${new Intl.NumberFormat('ko-KR').format(money.amount)} ${money.currencyCode}`;
};

const formatCount = (value: number | undefined | null): string =>
  new Intl.NumberFormat('ko-KR').format(value ?? 0);

type Tone = 'default' | 'success' | 'warn' | 'danger';

const TONE_FG: Record<Tone, string> = {
  default: 'var(--content-strong)',
  success: 'var(--status-success-fg)',
  warn: 'var(--status-warn-fg)',
  danger: 'var(--status-danger-fg)',
};

const KpiTile = ({ label, value, tone = 'default' }: { label: string; value: string; tone?: Tone }) => (
  <div className="flex flex-col gap-1 rounded-md p-3" style={{ background: 'var(--surface-1)' }}>
    <span className="text-xs text-[color:var(--content-muted)]">{label}</span>
    <span
      className="text-base font-semibold"
      style={{ color: TONE_FG[tone] }}
      data-testid="funding-dashboard-kpi-value"
    >
      {value}
    </span>
  </div>
);

export interface FundingDashboardKpisProps {
  data: FundingDashboard;
}

/** #2 펀딩 모니터링 KPI 카드 그리드 — dashboard 응답의 집계 지표를 8 타일로 노출. */
export const FundingDashboardKpis = ({ data }: FundingDashboardKpisProps) => {
  const { t } = useTranslation('funding');
  return (
    <section
      aria-label={t('funding.dashboard.title')}
      className="grid grid-cols-2 gap-2 md:grid-cols-4"
      data-testid="funding-dashboard-kpis"
    >
      <KpiTile label={t('funding.dashboard.kpiActive')} value={formatCount(data.activeCampaignCount)} />
      <KpiTile
        label={t('funding.dashboard.kpiSuccess')}
        value={formatCount(data.successCampaignCount)}
        tone="success"
      />
      <KpiTile
        label={t('funding.dashboard.kpiFailed')}
        value={formatCount(data.failedCampaignCount)}
        tone="danger"
      />
      <KpiTile label={t('funding.dashboard.kpiPledge')} value={formatCount(data.totalPledgeCount)} />
      <KpiTile
        label={t('funding.dashboard.kpiPaidCount')}
        value={formatCount(data.paidPledgeCount)}
        tone="success"
      />
      <KpiTile
        label={t('funding.dashboard.kpiPaidAmount')}
        value={formatMoney(data.totalPaidAmount)}
        tone="success"
      />
      <KpiTile
        label={t('funding.dashboard.kpiRefunded')}
        value={formatMoney(data.totalRefundedAmount)}
        tone="warn"
      />
      <KpiTile label={t('funding.dashboard.kpiAvg')} value={formatMoney(data.avgPledgeAmount)} />
    </section>
  );
};
